import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { ConfirmDialog } from "../components/Confirm";
import { EmptyState, ErrorState, LoadingBlock } from "../components/Feedback";
import { Icon } from "../components/Icon";
import { ProviderMark, oauthErrorMessage, startPath } from "../components/SignInProviders";
import { api } from "../lib/api";
import { copyText, downloadText } from "../lib/clipboard";
import { describeUserAgent, formatDate, formatDateTime, relativeTime } from "../lib/format";
import { useSession } from "../lib/session";
import { useToast } from "../lib/toast";
import { useAsync } from "../lib/useAsync";

const MIN_PASSWORD = 12;

export default function Account() {
  const { user, signOut, refresh } = useSession();
  const toast = useToast();
  const sessions = useAsync((signal) => api.sessions(signal), []);
  const identities = useAsync((signal) => api.identities(signal), []);
  const [params, setParams] = useSearchParams();
  // An account made through a sign-in provider has no password until the learner sets one.
  const hasPassword = user?.hasPassword !== false;

  const [unlinking, setUnlinking] = useState<string | null>(null);
  const [confirmUnlink, setConfirmUnlink] = useState<{ provider: string; label: string } | null>(null);

  // Coming back from linking a provider: say how it went, once, and tidy the address bar.
  useEffect(() => {
    const linked = params.get("linked");
    const failed = params.get("oauth_error");
    if (!linked && !failed) return;
    if (linked) toast.success("Account linked", "You can now sign in with it, followed by your authenticator code.");
    if (failed) toast.notify({ kind: "error", title: "That account was not linked", detail: oauthErrorMessage(failed) ?? undefined });
    setParams({}, { replace: true });
  }, [params, setParams, toast]);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changing, setChanging] = useState(false);

  const [regenPassword, setRegenPassword] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [newCodes, setNewCodes] = useState<string[] | null>(null);

  const [revoking, setRevoking] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<{ id: string; current: boolean } | null>(null);

  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;

  const changePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (mismatch) return;
    setChanging(true);
    try {
      await api.changePassword(hasPassword ? { currentPassword, newPassword } : { newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      await Promise.all([sessions.reload(), identities.reload(), refresh()]);
      toast.success(hasPassword ? "Password changed" : "Password set", "Every other device has been signed out.");
    } catch (error) {
      toast.reportError(error, "Your password could not be changed.");
    } finally {
      setChanging(false);
    }
  };

  const regenerate = async (event: React.FormEvent) => {
    event.preventDefault();
    setRegenerating(true);
    try {
      const result = await api.regenerateRecoveryCodes(hasPassword ? { password: regenPassword } : { code: regenPassword });
      setNewCodes(result.recoveryCodes);
      setRegenPassword("");
      toast.success("Ten new recovery codes", "The old ones no longer work.");
    } catch (error) {
      toast.reportError(error, "New recovery codes could not be generated.");
    } finally {
      setRegenerating(false);
    }
  };

  const unlink = async () => {
    if (!confirmUnlink) return;
    setUnlinking(confirmUnlink.provider);
    try {
      await api.unlinkIdentity(confirmUnlink.provider);
      await identities.reload();
      toast.success(`${confirmUnlink.label} unlinked`);
    } catch (error) {
      toast.reportError(error, "That account could not be unlinked.");
    } finally {
      setUnlinking(null);
      setConfirmUnlink(null);
    }
  };

  const revoke = async () => {
    if (!confirmRevoke) return;
    const { id, current } = confirmRevoke;
    setRevoking(id);
    try {
      await api.revokeSession(id);
      if (current) {
        await signOut();
        window.location.assign("/sign-in");
        return;
      }
      await sessions.reload();
      toast.success("Session signed out");
    } catch (error) {
      toast.reportError(error, "That session could not be signed out.");
    } finally {
      setRevoking(null);
      setConfirmRevoke(null);
    }
  };

  return (
    <AppShell>
      <div className="account">
        <header className="section-head">
          <h2>Account</h2>
          <span className="spread dim mono">{user?.email}</span>
        </header>

        <section className="panel" aria-labelledby="identity-heading">
          <div className="panel-head">
            <h3 id="identity-heading">Who you are</h3>
          </div>
          <div className="panel-pad">
            <dl className="kv">
              <dt>Name on your certificate</dt>
              <dd>{user?.displayName}</dd>
              <dt>Email address</dt>
              <dd className="mono">{user?.email}</dd>
              <dt>Role</dt>
              <dd>{user?.role === "admin" ? "Administrator" : "Learner"}</dd>
              <dt>Joined</dt>
              <dd>{formatDate(user?.createdAt)}</dd>
              <dt>Two-factor authentication</dt>
              <dd>
                {user?.mfaEnrolled ? (
                  <span className="mark mark-done">
                    <Icon name="check" size={13} className="glyph" />
                    On
                  </span>
                ) : (
                  <span className="mark mark-warn">Not set up</span>
                )}
              </dd>
            </dl>
          </div>
        </section>

        <section className="panel" aria-labelledby="password-heading">
          <div className="panel-head">
            <h3 id="password-heading">{hasPassword ? "Change your password" : "Set a password"}</h3>
          </div>
          <form className="panel-pad stack" onSubmit={changePassword} noValidate>
            {hasPassword ? (
              <div className="field">
                <label htmlFor="current">Current password</label>
                <input
                  id="current"
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
            ) : (
              <p className="muted">
                You sign in with a linked account. A password lets you sign in without it too.
              </p>
            )}
            <div className="field">
              <label htmlFor="new">New password</label>
              <input
                id="new"
                className="input"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="hint">At least {MIN_PASSWORD} characters.</p>
            </div>
            <div className="field">
              <label htmlFor="again">New password again</label>
              <input
                id="again"
                className="input"
                type="password"
                autoComplete="new-password"
                required
                aria-invalid={mismatch}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {mismatch ? <p className="field-error">The two passwords do not match.</p> : null}
            </div>
            <div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={changing || mismatch || newPassword.length < MIN_PASSWORD}
              >
                {changing ? <span className="spinner" /> : null}
                {hasPassword ? "Change password" : "Set password"}
              </button>
            </div>
            <p className="hint">Changing your password signs out every other device.</p>
          </form>
        </section>

        <section className="panel" aria-labelledby="providers-heading">
          <div className="panel-head">
            <h3 id="providers-heading">Sign-in methods</h3>
          </div>
          <div className="panel-pad stack">
            <p className="muted">
              Link an account to sign in with it instead of your password. Your authenticator code is
              still needed every time.
            </p>
            {identities.loading && !identities.data ? (
              <LoadingBlock label="Loading your sign-in methods" />
            ) : identities.error ? (
              <ErrorState error={identities.error} onRetry={() => void identities.reload()} />
            ) : identities.data ? (
              <div>
                {identities.data.available.map((p) => {
                  const linked = identities.data!.identities.find((i) => i.provider === p.id);
                  return (
                    <div className="session-row" key={p.id}>
                      <div className="row">
                        <ProviderMark id={p.id} />
                        <div>
                          <p className="ua">{p.label}</p>
                          <p className="when">
                            {linked
                              ? `Linked ${formatDate(linked.linkedAt)}${linked.email ? ` as ${linked.email}` : ""}`
                                + (linked.lastUsedAt ? ` — last used ${relativeTime(linked.lastUsedAt)}` : "")
                              : "Not linked"}
                          </p>
                        </div>
                      </div>
                      {linked ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={unlinking === p.id}
                          onClick={() => setConfirmUnlink({ provider: p.id, label: p.label })}
                        >
                          Unlink
                        </button>
                      ) : (
                        <a className="btn btn-sm" href={startPath(p.id, { intent: "link" })}>Link</a>
                      )}
                    </div>
                  );
                })}
                {identities.data.available.length === 0 ? (
                  <p className="dim">No sign-in providers are switched on.</p>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        <section className="panel" aria-labelledby="recovery-heading">
          <div className="panel-head">
            <h3 id="recovery-heading">Recovery codes</h3>
          </div>
          <div className="panel-pad stack">
            <p className="muted">
              Ten one-time codes that get you in when you cannot reach your authenticator app.
              Generating a new set immediately cancels the old one.
            </p>
            {newCodes ? (
              <>
                <ol className="recovery-codes">
                  {newCodes.map((code, index) => (
                    <li key={code}>
                      <b>{index + 1}</b>
                      <span>{code}</span>
                    </li>
                  ))}
                </ol>
                <div className="row">
                  <button
                    type="button"
                    className="btn"
                    onClick={() =>
                      downloadText("terraformed-life-recovery-codes.txt", newCodes.join("\n"))}
                  >
                    <Icon name="download" />
                    Download
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={async () => {
                      const ok = await copyText(newCodes.join("\n"));
                      if (ok) toast.success("Codes copied");
                    }}
                  >
                    <Icon name="copy" />
                    Copy
                  </button>
                  <button type="button" className="btn btn-quiet" onClick={() => setNewCodes(null)}>
                    Hide them
                  </button>
                </div>
                <p className="hint">
                  This is the only time these are shown. Store them somewhere you can reach without
                  your phone.
                </p>
              </>
            ) : (
              <form className="stack-tight" onSubmit={regenerate} noValidate>
                <div className="field">
                  <label htmlFor="regen">
                    {hasPassword ? "Confirm your password" : "A code from your authenticator app"}
                  </label>
                  <input
                    id="regen"
                    className="input"
                    type={hasPassword ? "password" : "text"}
                    inputMode={hasPassword ? undefined : "numeric"}
                    autoComplete={hasPassword ? "current-password" : "one-time-code"}
                    required
                    value={regenPassword}
                    onChange={(e) => setRegenPassword(e.target.value)}
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    className="btn"
                    disabled={regenerating || regenPassword.length === 0}
                  >
                    {regenerating ? <span className="spinner" /> : <Icon name="key" />}
                    Generate ten new codes
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>

        <section className="panel" aria-labelledby="sessions-heading">
          <div className="panel-head">
            <h3 id="sessions-heading">Where you are signed in</h3>
            <button type="button" className="btn btn-sm btn-quiet" onClick={() => void sessions.reload()}>
              <Icon name="refresh" size={13} />
              Refresh
            </button>
          </div>
          <div className="panel-pad">
            {sessions.loading && !sessions.data ? (
              <LoadingBlock label="Loading your sessions" />
            ) : sessions.error ? (
              <ErrorState error={sessions.error} onRetry={() => void sessions.reload()} />
            ) : sessions.data && sessions.data.sessions.length > 0 ? (
              <div>
                {sessions.data.sessions.map((row) => (
                  <div className="session-row" key={row.id}>
                    <div>
                      <p className="ua">
                        {describeUserAgent(row.userAgent)}
                        {row.current ? (
                          <span className="mark mark-active" style={{ marginLeft: "var(--s-5)" }}>
                            This device
                          </span>
                        ) : null}
                      </p>
                      <p className="when">
                        Last seen {relativeTime(row.lastSeenAt)} — started{" "}
                        {formatDateTime(row.createdAt)}
                        {row.ip ? ` from ${row.ip}` : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => setConfirmRevoke({ id: row.id, current: row.current })}
                      disabled={revoking === row.id}
                    >
                      {row.current ? "Sign out here" : "Sign out"}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon="user" title="No other sessions">
                You are only signed in on this device.
              </EmptyState>
            )}
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={!!confirmUnlink}
        title={`Unlink ${confirmUnlink?.label ?? "this account"}?`}
        confirmLabel="Unlink"
        destructive
        busy={!!unlinking}
        onConfirm={() => void unlink()}
        onCancel={() => setConfirmUnlink(null)}
      >
        <p>You will no longer be able to sign in with it. You can link it again at any time.</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!confirmRevoke}
        title={confirmRevoke?.current ? "Sign out on this device?" : "Sign out that session?"}
        confirmLabel="Sign out"
        destructive
        busy={!!revoking}
        onConfirm={() => void revoke()}
        onCancel={() => setConfirmRevoke(null)}
      >
        <p>
          {confirmRevoke?.current
            ? "You will be taken back to the sign-in page and will need your password and a code."
            : "That device will need to sign in again with a password and a code. Nothing you have saved is affected."}
        </p>
      </ConfirmDialog>
    </AppShell>
  );
}
